import React from 'react';
import { useHistory } from 'react-router-dom';
import ClearIcon from '@mui/icons-material/Clear';
import EngineeringOutlinedIcon from '@mui/icons-material/EngineeringOutlined';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import { useProject } from '@hooks/api/projects/useProject';
import { useTranslation } from '@hooks/useTranslation';
import { AssistantTurn } from '@models/assistant.model';
import { Composer } from './Composer';
import { ActionCard } from './ActionCard';
import { MessageList } from './MessageList';
import { useAssistantSession } from './useAssistantSession';

const EXAMPLE_PROMPT_KEYS = ['examplePrompt1', 'examplePrompt2', 'examplePrompt3'] as const;

interface AssistantChatProps {
  session: ReturnType<typeof useAssistantSession>;
}

/**
 * Sprint 37, story-196/199. The chat pane: header (title, fixed "About: <Project>" label with a
 * close "x" that only navigates to `/assistant`), message list or ready empty state, action card,
 * composer. The project binding is fixed at conversation creation, so there is deliberately no
 * control here that changes it.
 */
export const AssistantChat: React.FC<AssistantChatProps> = ({ session }) => {
  const t = useTranslation('assistant');
  const history = useHistory();
  const { conversation, projectParam } = session;

  // Pre-send (`/assistant?project=<id>`) the name comes from the project query; afterwards from
  // the conversation DTO (which is null once the project is deleted or out of the caller's scope).
  const { data: routeProject } = useProject(conversation ? undefined : projectParam);
  const boundProjectName = conversation ? conversation.projectName : routeProject?.name;
  const isBound = !!(conversation ? conversation.projectId : projectParam);

  const baseTurns = conversation?.turns ?? [];
  const turns: AssistantTurn[] = session.pendingUserText
    ? [
        ...baseTurns,
        {
          role: 'USER',
          kind: 'MESSAGE',
          text: session.pendingUserText,
          at: new Date().toISOString(),
          toolsUsed: [],
        },
      ]
    : baseTurns;
  const showEmptyState = turns.length === 0 && session.phase === 'idle' && !session.sending;
  const status = session.sending && !conversation ? 'THINKING' : conversation?.status;

  return (
    <div
      className="flex flex-col h-full min-h-0 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4"
      data-testid="assistant-chat">
      <div className="flex items-start justify-between gap-3 pb-3 mb-3 border-b border-gray-100 dark:border-gray-700">
        <div className="min-w-0">
          <h1 className="text-base font-semibold truncate" title={conversation?.title || t.untitled}>
            {conversation?.title || t.untitled}
          </h1>
          {isBound && boundProjectName && (
            <div
              className="flex items-center gap-1 text-xs text-gray-500 mt-0.5"
              data-testid="assistant-project-label">
              <EngineeringOutlinedIcon sx={{ fontSize: 14 }} />
              <span>{t.aboutProject.replace('{project}', boundProjectName)}</span>
              <button
                type="button"
                aria-label={t.leaveProjectChat}
                title={t.leaveProjectChat}
                onClick={() => history.push('/assistant')}
                className="ml-1 text-gray-400 hover:text-gray-700"
                data-testid="assistant-leave-project">
                <ClearIcon sx={{ fontSize: 14 }} />
              </button>
            </div>
          )}
        </div>
      </div>

      {showEmptyState ? (
        <div className="flex-1 min-h-0 overflow-y-auto flex flex-col items-center justify-center text-center gap-3">
          <SmartToyOutlinedIcon sx={{ fontSize: 40 }} className="text-gray-400" />
          <div className="text-base font-semibold">{t.welcomeTitle}</div>
          <div className="text-sm text-gray-600 dark:text-gray-300 max-w-md">{t.emptyStateTitle}</div>
          <div className="flex flex-wrap gap-2 justify-center max-w-xl">
            {EXAMPLE_PROMPT_KEYS.map((key) => (
              <button
                key={key}
                type="button"
                onClick={() => void session.sendMessage(t[key])}
                className="text-xs rounded-lg border border-gray-200 dark:border-gray-700 px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-700"
                data-testid={`assistant-example-${key}`}>
                {t[key]}
              </button>
            ))}
          </div>
        </div>
      ) : (
        <MessageList
          turns={turns}
          status={status}
          errorCode={conversation?.errorCode}
          onRetry={() => void session.retry()}
        />
      )}

      {conversation?.pendingAction && (
        <div className="mt-3">
          <ActionCard
            pendingAction={conversation.pendingAction}
            onConfirm={() => void session.confirmAction()}
            onDismiss={() => void session.dismissAction()}
            busy={session.confirming || session.dismissing}
          />
        </div>
      )}

      <Composer
        onSend={(text) => void session.sendMessage(text)}
        disabled={session.phase === 'thinking' || session.sending}
      />
    </div>
  );
};

export default AssistantChat;
